import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow, GoldRule, PractitionerCard, ConcernTile, CTABand, Photo, scrollToId } from "@/components/Shared";
import { PHYSICIANS, JOURNAL_POSTS, CLINIC, INTERNATIONAL_STANDFIRST } from "@/data/content";
import { CONCERNS } from "@/data/concerns";
import { PHOTOS } from "@/data/photos";
import { ConciergeButton } from "@/components/Concierge";
import { Layers, CircleDot, Stethoscope, ArrowRight } from "lucide-react";

const HOME_CONCERN_SLUGS = [
  "skin/pigmentation-and-sun-damage",
  "face/jawline-and-jowls",
  "skin/skin-quality-and-texture",
  "hair/male-pattern",
  "body/excessive-sweating",
  "life-stages/your-first-treatment",
];

const ease = [0.22, 1, 0.36, 1];

const HERO_IMAGE = "/images/hero-sunset.webp";

const HeroBackdrop = () => (
  <div className="absolute inset-0" aria-hidden="true">
    <img
      src={HERO_IMAGE}
      alt=""
      loading="eager"
      fetchPriority="high"
      decoding="async"
      className="filmic absolute inset-0 w-full h-full object-cover"
      style={{ objectPosition: "50% 60%" }}
      data-testid="hero-image"
    />
    <div className="absolute inset-0 bg-[rgba(35,58,53,0.10)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_62%_58%_at_50%_46%,rgba(35,58,53,0.42),transparent_72%)]" />
    <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[rgba(35,58,53,0.5)] to-transparent" />
  </div>
);

const Hero = () => (
  <section className="relative min-h-[80svh] overflow-hidden flex items-center justify-center bg-deep-sea" data-testid="hero-section">
    <HeroBackdrop />

    <div className="gold-frame inset-4 sm:inset-6 z-10" aria-hidden="true" />
    <div className="gold-frame inset-7 sm:inset-10 opacity-60 z-10" aria-hidden="true" />

    <div className="relative z-10 text-center px-8 py-24 max-w-3xl">
      <div>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.1, delay: 0.2 }} className="eyebrow eyebrow-dark" data-testid="hero-eyebrow">
        Cottesloe · Doctor-run aesthetics
      </motion.p>
      </div>

      <div>
      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.85, ease }}
        className="text-warm-white mt-10 [text-shadow:0_2px_24px_rgba(35,58,53,0.55)]"
        data-testid="hero-heading"
      >
        Aesthetics by the ocean.
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.95, ease }}
        className="font-serif italic font-light text-warm-white/95 text-[28px] sm:text-[32px] leading-snug mt-4 [text-shadow:0_1px_16px_rgba(35,58,53,0.55)]"
        data-testid="hero-positioning-line"
      >
        A medical practice, not a beauty clinic.
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.05, ease }}
        className="text-warm-white/90 text-base sm:text-lg mt-6 max-w-xl mx-auto leading-relaxed [text-shadow:0_1px_14px_rgba(35,58,53,0.55)]"
        data-testid="hero-standfirst"
      >
        A doctor-run aesthetics practice inside an established Cottesloe medical centre. Honest advice, natural
        results, and a consultation before anything else.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.2, ease }}
        className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-10"
      >
        <ConciergeButton testId="hero-book-button" className="btn-gold px-7 py-3.5 text-sm font-medium tracking-[0.06em]" />
        <Link
          to="/treatments"
          data-testid="hero-treatments-link"
          className="inline-flex items-center gap-2 text-warm-white text-[12px] tracking-[0.14em] uppercase font-medium border-b border-warm-white/60 pb-1 hover:border-gold hover:text-gold transition-colors duration-300"
        >
          Explore treatments
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Link>
      </motion.div>
      </div>
    </div>
  </section>
);

const DOCTOR_RUN_POINTS = [
  "A doctor assesses you before any medical treatment",
  "Every treatment plan is made and overseen by a doctor",
  "Clinical protocols are written by our doctors, and every practitioner works within them",
  "A doctor on site every treatment day",
  "Honest advice, including when treatment isn't right for you",
  "Long-term plans rather than one-off appointments",
];

const DoctorRun = () => (
  <section id="doctor-run" className="bg-warm-white" data-testid="doctor-run-section">
    <div className="shell py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-11 gap-14 lg:gap-16 items-center">
      <Reveal className="lg:col-span-4">
        <Eyebrow className="mb-3">Doctor-run</Eyebrow>
        <h2 className="text-deep-sea">
          Not simply doctor-owned. Doctor-run.
        </h2>
        <GoldRule className="mt-6" />
        <p className="text-ink/80 mt-6 leading-[1.8]">
          There is a difference between an aesthetics practice that is doctor-owned and one that is genuinely
          doctor-run. At Lumière, our doctors aren't the names behind the business — they are at the heart of how it
          works, from assessment and planning to clinical decisions and ongoing care. Aesthetics should begin with
          medicine, not marketing.
        </p>
        <h3 className="text-deep-sea mt-10">What this means</h3>
        <ul className="mt-5 space-y-3.5" data-testid="doctor-run-list">
          {DOCTOR_RUN_POINTS.map((point, i) => (
            <li key={i} className="flex gap-4 text-ink/85 text-lg leading-snug" data-testid={`doctor-run-point-${i}`}>
              <span className="w-5 h-px bg-hairline mt-3.5 shrink-0" aria-hidden="true" />
              {point}
            </li>
          ))}
        </ul>
        <p className="text-soft-grey text-sm mt-8 leading-relaxed" data-testid="doctor-run-note">
          Skin, laser and device treatments are delivered by our registered nurse under doctor-written protocols.
          Each practitioner's profile shows the treatments they provide.
        </p>
        <Link to="/team" data-testid="doctor-run-team-link" className="link-sea inline-flex items-center gap-2 mt-8 text-sm font-medium no-underline">
          Meet the Lumière Team
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Link>
      </Reveal>
      <Reveal delay={0.12} className="lg:col-span-7">
        <Photo photo={PHOTOS.doctorRun} className="aspect-[4/3] lg:aspect-[5/4]" frame testId="doctor-run-photo" />
      </Reveal>
    </div>
  </section>
);

const DOOR_CARDS = [
  {
    id: "treatments",
    icon: Layers,
    title: "Treatments",
    text: "From injectables and threads to laser, body and hair. Every treatment explained plainly.",
    to: "/treatments",
    link: "Explore treatments",
    img: { src: "/images/treatment-room.webp", alt: "A Lumière treatment room — white chair, oak floor and sheer curtains in daylight", pos: "50% 50%" },
  },
  {
    id: "concerns",
    icon: CircleDot,
    title: "Concerns",
    text: "Start with what's bothering you. We'll tell you what can and can't be done.",
    to: "/concerns",
    link: "Browse concerns",
    img: { src: "/images/models/woman-linen-profile.webp", alt: "A woman's cheek and jaw in soft window light", pos: "50% 40%" },
  },
  {
    id: "team",
    icon: Stethoscope,
    title: "The Lumière Team",
    text: "Our physicians, a registered nurse and our practice manager. See who you should see.",
    to: "/team",
    link: "Meet the team",
    img: { src: "/images/team/dr-kristy-truong-scrubs.webp", alt: "Dr Kristy Truong at Lumière", pos: "50% 22%" },
  },
];

const ThreeDoors = () => (
  <section className="bg-cream" data-testid="three-doors">
    <div className="shell py-24 lg:py-28">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {DOOR_CARDS.map((door, i) => (
          <Reveal key={door.id} delay={i * 0.1}>
            <Link to={door.to} data-testid={`door-${door.id}`} className="card-float overflow-hidden block h-full group transition-transform duration-500 hover:-translate-y-1.5">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={door.img.src}
                  alt={door.img.alt}
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: door.img.pos }}
                  className="filmic w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <div className="px-9 pb-9">
                <div className="w-14 h-14 rounded-full border border-gold bg-cream flex items-center justify-center -mt-7 relative z-10 shadow-[0_2px_10px_rgba(58,58,58,0.12)]">
                  <door.icon className="w-6 h-6 text-gold" strokeWidth={1.25} />
                </div>
                <h3 className="text-deep-sea mt-5">{door.title}</h3>
                <p className="text-ink/75 text-sm mt-3 leading-relaxed">{door.text}</p>
                <span className="link-sea inline-flex items-center gap-2 mt-5 text-sm font-medium no-underline">
                  {door.link}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PILLARS = [
  { title: "Doctor-run", line: "Not simply doctor-owned. A practice built around medical expertise." },
  { title: "Consultation before treatment", line: "We don't begin with a syringe, a laser or a menu. We begin with you." },
  { title: "Less, when less is more", line: "Our aim isn't to change your face. It's to refine what is already yours." },
  { title: "Long-term aesthetics", line: "Not individual appointments — long-term plans." },
  { title: "Medicine, beautifully delivered", line: "Clinical care in a calm, private, considered setting." },
];

const LumiereDifference = () => (
  <section className="bg-cream" data-testid="lumiere-difference">
    <div className="shell py-24 lg:py-32">
      <Reveal className="max-w-2xl">
        <Eyebrow className="mb-3">The Lumière Difference</Eyebrow>
        <h2 className="text-deep-sea">
          Experience the Lumière difference.
        </h2>
        <GoldRule className="mt-6" />
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12 mt-16">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <div className="border-t border-hairline/60 pt-6" data-testid={`pillar-${i}`}>
              <span className="font-serif font-light text-hairline text-[64px] lg:text-[72px] leading-none block" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="text-deep-sea mt-4">{p.title}</h3>
              <p className="text-ink/75 text-sm mt-3 leading-relaxed">{p.line}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PullQuote = () => (
  <section className="bg-deep-sea relative overflow-hidden" data-testid="pull-quote">
    <div className="gold-frame inset-5 sm:inset-7 opacity-50" aria-hidden="true" />
    <div className="shell py-24 lg:py-28 relative">
      <Reveal>
        <blockquote className="max-w-4xl">
          <p className="font-serif italic font-light text-warm-white text-3xl sm:text-4xl lg:text-[44px] leading-[1.3]">
            "The most useful thing we can offer isn't a treatment. It's an honest opinion."
          </p>
        </blockquote>
      </Reveal>
    </div>
  </section>
);

const DISCRETION_POINTS = [
  {
    title: "An ordinary appointment",
    text: "Lumière practises inside an established Cottesloe medical centre. Arriving here looks like any other visit to the building — because that is exactly what it is.",
  },
  {
    title: "A spaced diary",
    text: "Appointments are spaced so the practice stays quiet. Your time in the room is yours — unhurried, and unshared.",
  },
  {
    title: "Confidential, clinically",
    text: "Your record here is a medical record, held to the same standard of confidence as any other in the building. Nothing is shared, published or discussed beyond your care.",
  },
];

const Discretion = () => (
  <section className="bg-warm-white" data-testid="discretion-section">
    <div className="shell py-24 lg:py-28 grid grid-cols-1 lg:grid-cols-11 gap-14">
      <Reveal className="lg:col-span-4">
        <Eyebrow className="mb-3">Discretion</Eyebrow>
        <h2 className="text-deep-sea">Quietly, by design.</h2>
        <GoldRule className="mt-6" />
        <p className="standfirst max-w-xs">
          Aesthetics is personal. The practice is arranged so your visit can be too.
        </p>
      </Reveal>
      <div className="lg:col-span-7 space-y-10">
        {DISCRETION_POINTS.map((item, i) => (
          <Reveal key={item.title} delay={i * 0.1}>
            <div className="flex gap-6 border-b border-hairline/40 pb-10 last:border-0 last:pb-0" data-testid={`discretion-item-${i}`}>
              <span className="font-serif italic text-gold text-2xl font-light shrink-0 w-8" aria-hidden="true">{i + 1}</span>
              <div>
                <h3 className="text-deep-sea">{item.title}</h3>
                <p className="text-ink/75 text-sm mt-2 leading-relaxed">{item.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FindYourPhysician = () => (
  <section className="bg-cream" data-testid="find-your-physician">
    <div className="shell py-24 lg:py-28">
      <Reveal className="max-w-3xl">
        <Eyebrow className="mb-3">The Lumière Team</Eyebrow>
        <h2 className="text-deep-sea">
          International expertise, here in Cottesloe.
        </h2>
        <GoldRule className="mt-6" />
        <p className="standfirst" data-testid="international-standfirst">{INTERNATIONAL_STANDFIRST}</p>
      </Reveal>
      <Reveal className="mt-14">
        <h3 className="text-deep-sea">Find your physician</h3>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {PHYSICIANS.map((member, i) => (
          <PractitionerCard key={member.id} member={member} index={i} />
        ))}
      </div>
      <Reveal className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8 border-t border-hairline/50 pt-10">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <p className="font-serif italic text-deep-sea text-xl">Not sure where to start? That's what we're here for.</p>
          <ConciergeButton testId="physicians-consult-button" className="btn-gold px-7 py-3.5 text-sm font-medium tracking-[0.06em] self-start sm:self-auto" />
        </div>
        <Link to="/team" data-testid="meet-full-team-link" className="link-sea text-sm font-medium shrink-0 no-underline inline-flex items-center gap-2">
          Meet the full team
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const CommonConcerns = () => {
  const concerns = HOME_CONCERN_SLUGS.map((path) => {
    const [family, slug] = path.split("/");
    return CONCERNS.find((c) => c.family === family && c.slug === slug);
  }).filter(Boolean);

  return (
    <section className="bg-warm-white" data-testid="common-concerns">
      <div className="shell py-24 lg:py-28">
        <Reveal className="text-center mb-14">
          <h2 className="text-deep-sea">Start with what's bothering you</h2>
          <GoldRule className="mx-auto mt-6" />
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {concerns.map((c, i) => (
            <ConcernTile key={c.slug} concern={c} index={i} />
          ))}
        </div>
        <Reveal className="text-center mt-10">
          <Link to="/concerns" data-testid="all-concerns-link" className="link-sea text-sm font-medium">Browse all concerns</Link>
        </Reveal>
      </div>
    </section>
  );
};

const ThePractice = () => (
  <section className="bg-cream" data-testid="the-practice">
    <div className="shell py-24 lg:py-28 grid grid-cols-1 lg:grid-cols-11 gap-14 items-center">
      <Reveal className="lg:col-span-4">
        <Photo photo={PHOTOS.clinicReception} className="aspect-[4/3]" eager={false} testId="practice-photo" />
      </Reveal>
      <Reveal delay={0.12} className="lg:col-span-7">
        <Eyebrow className="mb-3">The practice</Eyebrow>
        <h2 className="text-deep-sea">Marine Parade, Cottesloe</h2>
        <p className="text-ink/80 mt-6 leading-[1.8] max-w-2xl">
          We practise from an established medical centre on Marine Parade — curved cream walls, soft light, and the
          kind of quiet that makes a consultation feel like a conversation. General practice and pathology are in the
          same building, and every treatment day has a doctor on site with emergency equipment to hand.
        </p>
        <p className="text-ink/80 mt-4 leading-[1.8] max-w-2xl" data-testid="who-comes-here-text">
          Brides counting backwards from a date. Men who would never say the word aesthetics out loud. Sailors
          carrying thirty summers of sun. Sixty-year-olds who simply want to look like themselves on a good day.
          Whoever you are, the first step is the same — a consultation, a proper history, and honest advice.
        </p>
        <p className="text-ink/80 mt-4 leading-[1.8] max-w-2xl">
          {CLINIC.parking}. Two minutes from the beach.
        </p>
        <p className="text-soft-grey text-sm mt-6">{CLINIC.hoursText}</p>
        <p className="text-soft-grey text-sm mt-1">
          <a href={`tel:${CLINIC.phoneTel}`} data-testid="practice-phone-link" className="hover:text-gold transition-colors duration-300">{CLINIC.phone}</a>
        </p>
        <Link to="/contact" data-testid="practice-contact-link" className="link-sea inline-block mt-6 text-sm font-medium">Find us</Link>
      </Reveal>
    </div>
  </section>
);

const JournalTeaser = () => (
  <section className="bg-warm-white" data-testid="journal-teaser">
    <div className="shell py-24 lg:py-28">
      <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
        <div>
          <Eyebrow className="mb-3">Journal</Eyebrow>
          <h2 className="text-deep-sea">From the practice</h2>
        </div>
        <Link to="/journal" data-testid="journal-link" className="link-sea text-sm font-medium shrink-0">All articles</Link>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {JOURNAL_POSTS.slice(0, 3).map((post, i) => (
          <Reveal key={post.slug} delay={i * 0.1}>
            <Link to={`/journal/${post.slug}`} data-testid={`journal-card-${post.slug}`} className="card-float block overflow-hidden group h-full">
              <Photo photo={PHOTOS[post.photo]} className="aspect-[16/10]" width={800} />
              <div className="p-7">
                <Eyebrow>{post.category}</Eyebrow>
                <h3 className="text-deep-sea mt-2 group-hover:text-sea-green transition-colors duration-300">{post.title}</h3>
                <p className="text-ink/70 text-sm mt-2 leading-relaxed">{post.excerpt}</p>
                <p className="text-soft-grey text-xs mt-4">{post.date} · {post.readTime}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <SEO
      title="Lumière Aesthetics Cottesloe | Doctor-Run Aesthetics, Perth"
      description="Doctor-run aesthetics inside an established Cottesloe medical centre. A medical practice, not a beauty clinic. Consultation first."
      ogTitle="Lumière Aesthetics — International expertise, here in Cottesloe"
    />
    <Hero />
    <DoctorRun />
    <ThreeDoors />
    <LumiereDifference />
    <PullQuote />
    <Discretion />
    <FindYourPhysician />
    <CommonConcerns />
    <ThePractice />
    <JournalTeaser />
    <CTABand />
  </>
);

export default Home;
